<template>
  <PriceDisplayCell
    v-if="hasBackbox && market.currentBackboxPrice?.amount"
    :additionalInfo="i18n(translations.hasBackboxPriceLabel)"
    currentState="success"
    :formattedPrice="
      i18n.price(market.currentBackboxPrice?.amount, {
        currency: market.currentBackboxPrice?.currency as Currency,
      })
    "
    :tagContent="i18n(translations.hasBackboxTagLabel)"
    type="backbox"
  />

  <div v-if="isOffline">
    <RevLink
      class="hover:no-underline"
      :to="listingDetailsUrl"
      :underlined="false"
    >
      <Pill
        custom-class="bg-surface-default-low border-static-danger-hi border-[0.1rem] hover:bg-action-default-mid-hover hover:cursor-pointer"
        size="medium"
        :tooltipContent="i18n(translations.listingIsOfflineTooltip)"
      >
        <template #default="{ iconSize, textSize }">
          <IconBlocked class="text-static-danger-hi" :size="iconSize" />
          <span :class="textSize">
            {{ i18n(translations.listingIsOfflineLabel) }}
          </span>
        </template>
      </Pill>
    </RevLink>
  </div>

  <div
    v-if="doesNotHaveBackbox && market.currentBackboxPrice?.amount"
    class="w-full"
  >
    <PriceDisplayCell
      v-if="hasBackWithOtherListing"
      :additionalInfo="i18n(translations.otherlistingHasBackboxLabel)"
      currentState="pending"
      :formattedPrice="
        i18n.price(market.currentBackboxPrice?.amount, {
          currency: market.currentBackboxPrice?.currency as Currency,
        })
      "
      :tagContent="i18n(translations.defaultBackboxTagLabel)"
      type="backbox"
    />
    <PriceDisplayCell
      v-else-if="market.backboxPrice?.amount"
      :additionalInfo="i18n(translations.winBackBox)"
      currentState="default"
      :formattedPrice="
        i18n.price(market.backboxPrice?.amount, {
          currency: market.backboxPrice?.currency as Currency,
        })
      "
      :tagContent="i18n(translations.defaultBackboxTagLabel)"
      type="backbox"
    />
  </div>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed } from 'vue'

import type { Currency } from '@backmarket/http-api'
import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevLink } from '@ds/components/Link'
import { IconBlocked } from '@ds/icons/IconBlocked'

import type { ListingV2, MarketListing } from '~/api/listings/types'
import {
  QUERY_MODAL_NAME,
  QUERY_MODAL_TAB_NAME,
  TABS_INFO,
} from '~/scopes/listings/pages/index/components/ListingDetailsDrawer/ListingDetailsDrawer.constants'

import Pill from '../../../Pill/Pill.vue'
import translations from '../../ExpandedListing.translations'
import PriceDisplayCell from '../PriceDisplayCell/PriceDisplayCell.vue'

const props = defineProps<{
  market: MarketListing & {
    countryCode: MarketCountryCode
    backBoxIndicator: 'withBackbox' | 'withoutBackbox' | 'offline'
  }
  listing: ListingV2
}>()

const i18n = useI18n()
const route = useRoute()

const hasBackbox = computed(() => {
  return props.market.backBoxIndicator === 'withBackbox'
})

const doesNotHaveBackbox = computed(() => {
  return props.market.backBoxIndicator === 'withoutBackbox'
})

const hasBackWithOtherListing = computed(() => {
  return props.market.hasBackboxWithOtherListing
})

const isOffline = computed(() => {
  return props.market.backBoxIndicator === 'offline'
})

const listingDetailsUrl = computed(() => ({
  ...route,
  query: route.query,
  hash: `#${QUERY_MODAL_NAME}=${props.listing.id}#${QUERY_MODAL_TAB_NAME}=${TABS_INFO[0].tabName}`,
}))
</script>
